import React, { useState, useEffect } from "react";
import {
  ModalBackdrop,
  ModalCard,
  ModalHeader,
  ModalHeaderContent,
  ModalAccent,
  ModalTitle,
  ModalSubtitle,
  ModalBody,
  FieldGroup,
  FieldLabel,
  SelectInput,
  ModalCloseBtn,
  FieldHint,
  ModalFooter,
  ModalCancelBtn,
  ModalCreateBtn,
  G,
  FieldError,
  IcoX,
  IcoMove,
} from "./styles";
import { isPrivateAssignment } from "./CoreBundle";

const MoveDocumentModal = ({
  open,
  onClose,
  bundles,
  assignment,
  sourceBundleId,
  sourceSfCode,
  onMoved,
  strictPrivate = false,
  ownerId,
}) => {
  const [targetBundleId, setTargetBundleId] = useState("");
  const [targetSfCode, setTargetSfCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTargetBundleId(sourceBundleId || "");
      setTargetSfCode("");
      setError("");
      setSubmitting(false);
    }
  }, [open, sourceBundleId]);

  if (!open || !assignment) return null;

  // Filter destination bundles - Allow all caseManager bundles owned by this user
  const destinationBundles = bundles.filter(
    (b) => b.ownerRole === "caseManager" && String(b.ownerId) === String(ownerId)
  );

  const targetBundle = destinationBundles.find(
    (b) => String(b.id) === String(targetBundleId),
  );

  const subFoldersList = [...(targetBundle?.bundles || [])];
  const hasMain = subFoldersList.some((sf) => sf.isMainFolder);
  if (!hasMain && targetBundle?.hasBundleFolders) {
    subFoldersList.push({
      code: "__flat__",
      name: "Without Sub-folder",
      isMainFolder: true,
      visibilityType: targetBundle?.visibilityType || "me",
      assignments: [],
    });
  }

  const targetSubFolders = subFoldersList.filter((sf) => {
    // Exclude the sub-folder the file is currently in only if it is the same bundle
    if (
      String(targetBundleId) === String(sourceBundleId) &&
      String(sf.code) === String(sourceSfCode)
    ) {
      return false;
    }
    
    if (!strictPrivate) return true;
    return sf.visibilityType === "strictMode" || sf.visibilityType === "me";
  });

  const handleMove = async () => {
    if (!targetBundleId) {
      setError("Select a destination bundle.");
      return;
    }
    if (!targetSfCode) {
      setError("Select a destination sub-folder.");
      return;
    }
    if (
      String(targetBundleId) === String(sourceBundleId) &&
      targetSfCode === sourceSfCode
    ) {
      setError("Destination is the same as source.");
      return;
    }

    if (strictPrivate && !isPrivateAssignment(assignment)) {
      setError(
        "Only private documents can be moved inside My Private Bundles.",
      );
      return;
    }

    // Optional: Extra check for caseManager ownership
    const targetBundleData = destinationBundles.find(
      (b) => String(b.id) === String(targetBundleId),
    );
    if (targetBundleData?.ownerRole !== "caseManager") {
      setError("You can only move documents to Case Manager bundles.");
      return;
    }

    setSubmitting(true);
    try {
      await onMoved({
        assignment,
        sourceBundleId,
        sourceSfCode,
        targetBundleId,
        targetSfCode,
      });
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to move document.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalAccent style={{ background: G.amber }} />
          <ModalHeaderContent>
            <ModalTitle>Move Document</ModalTitle>
            <ModalSubtitle>
              Moving{" "}
              <strong>
                {assignment.userLabel || `Tab ${assignment.tabNum}`}
              </strong>
              . 
            </ModalSubtitle>
          </ModalHeaderContent>
          <ModalCloseBtn onClick={onClose}>
            <IcoX size={14} color={G.slate500} />
          </ModalCloseBtn>
        </ModalHeader>

         <ModalBody>
          <FieldGroup>
            <FieldLabel>
              DESTINATION BUNDLE <span className="required-star">*</span>
            </FieldLabel>
            <SelectInput
              value={targetBundleId}
              onChange={(e) => {
                setTargetBundleId(e.target.value);
                setTargetSfCode("");
                setError("");
              }}
              style={{
                fontFamily: "openSans-SemiBold, sans-serif",
                color: "#293461",
              }}
            >
              <option value="" style={{ fontFamily: "openSans-SemiBold, sans-serif", color: "#293461" }}>
                Select Bundle
              </option>
              {destinationBundles.map((b) => (
                <option
                  key={b.id}
                  value={b.id}
                  style={{ fontFamily: "openSans-SemiBold, sans-serif", color: "#293461" }}
                >
                  {b.name} ({b.bundleCode})
                </option>
              ))}
            </SelectInput>
          </FieldGroup>

          {targetBundleId && (
            <FieldGroup>
              <FieldLabel>
                DESTINATION SUB-FOLDER <span className="required-star">*</span>
              </FieldLabel>
              {targetSubFolders.length === 0 ? (
                <FieldHint style={{ color: G.red }}>
                  This bundle has no sub-folders. Create one first.
                </FieldHint>
              ) : (
                <SelectInput
                  value={targetSfCode}
                  onChange={(e) => {
                    setTargetSfCode(e.target.value);
                    setError("");
                  }}
                  style={{
                    fontFamily: "openSans-SemiBold, sans-serif",
                    color: "#293461",
                  }}
                >
                  <option
                    value=""
                    style={{
                      fontFamily: "openSans-SemiBold, sans-serif",
                      color: "#293461",
                    }}
                  >
                    Select
                  </option>
                  {targetSubFolders.map((sf) => {
                    const isMain = sf.isMainFolder || sf.code === "__main__" || sf.code === "__flat__";
                    const displayName = isMain ? "Within Bundle" : `${sf.code} — ${sf.name}`;
                    return (
                      <option
                        key={sf.code}
                        value={sf.code}
                        style={{
                          fontFamily: "openSans-SemiBold, sans-serif",
                          color: "#293461",
                        }}
                      >
                        {displayName}
                      </option>
                    );
                  })}
                </SelectInput>
              )}
            </FieldGroup>
          )}

          {error && <FieldError>{error}</FieldError>}
        </ModalBody>

        <ModalFooter>
          <ModalCancelBtn onClick={onClose}>Cancel</ModalCancelBtn>
          <ModalCreateBtn
            onClick={handleMove}
            disabled={submitting}
            style={{ background: G.amber, opacity: submitting ? 0.7 : 1 }}
          >
            <IcoMove size={12} /> {submitting ? "Moving…" : "Move"}
          </ModalCreateBtn>
        </ModalFooter>
      </ModalCard>
    </ModalBackdrop>
  );
};

export default MoveDocumentModal;
